/**
 * Counter.
 *
 * @author: FelixKiesewetter <info@felixkiesewetter.de>
 * @lastModified: 2026-06-16 Bugfixes and TS stuff, also accepts prefixes like "> 2.000".
 * @package fk_js/counter
 * @version: 2.3.0
 */
class Counter
{

    private duration :number = 700;
    private intersectionObservers :IntersectionObserver[] = [];
    private nodes :Array<Node> = [];
    private steps :number = 50;
    private spaceBeforeUnit :boolean = true;

    constructor(
        from :string|Node|NodeList,
        auto :boolean = true
    )
    {
        if (auto) {

            const intersectionObserverInOptions :{} = {
                root: null,
                rootMargin: '-15% 0px',
                threshold: 0
            };

            const intersectionObserverOutOptions :{} = {
                root: null,
                rootMargin: '0px 0px',
                threshold: 0
            };

            const intersectionObserverInCallback = (entries :IntersectionObserverEntry[]) :void => {
                entries.forEach((entry :IntersectionObserverEntry) :void => {
                    const number :HTMLElement = entry.target as HTMLElement;
                    if (entry.isIntersecting) {
                        this.increment(number);
                    }
                });
            };

            const intersectionObserverOutCallback = (entries :IntersectionObserverEntry[]) :void => {
                entries.forEach(entry => {
                    const number :Element = entry.target;
                    if (!entry.isIntersecting) {
                        number.textContent = '0';
                    }
                });
            };

            this.intersectionObservers.push(new IntersectionObserver(intersectionObserverInCallback, intersectionObserverInOptions));
            this.intersectionObservers.push(new IntersectionObserver(intersectionObserverOutCallback, intersectionObserverOutOptions));
        }

        let nodes;
        if (NodeList === from.constructor) {
            nodes = Object.values(from);
        }
        else if ('object' === typeof from) {
            nodes = new Array(from);
        }
        else {
            nodes = Object.values(document.querySelectorAll(from));
        }

        // Parse nodes and get numerical values, units and prefixes:
        // @ts-ignore
        nodes?.forEach((node :Node) :void  => {
            const matchNumbersAndUnits :RegExpMatchArray|null|undefined = node.textContent?.match(Counter.REGEXP_NUMBERS_AND_UNITS);
            if (!matchNumbersAndUnits) {
                return;
            }
            this.nodes.push(node);
            (node as HTMLElement).dataset.target = this.deToEnNumberFormat(matchNumbersAndUnits.groups?.value || '');
            if (matchNumbersAndUnits.groups?.prefix) {
                (node as HTMLElement).dataset.prefix = this.deToEnNumberFormat(matchNumbersAndUnits.groups.prefix);
            }
            if (matchNumbersAndUnits.groups?.unit) {
                (node as HTMLElement).dataset.unit = this.deToEnNumberFormat(matchNumbersAndUnits.groups.unit);
            }
            (node as HTMLElement).dataset.decimals = (node.textContent?.split(',')[1]?.length || 0).toString();
            node.textContent = '0';
            if (auto) {
                this.intersectionObservers[0].observe(node as HTMLElement);
                this.intersectionObservers[1].observe(node as HTMLElement);
            }
        });
    }

    // ##############
    // Public methods
    // ##############

    public count() :void
    {
        this.nodes?.forEach((node :Node) :void  => {
            this.increment(node as HTMLElement, 0);
        });
    }

    // ###############
    // Private methods
    // ###############

    private deToEnNumberFormat(deNumberFormat :string) :string
    {
        return deNumberFormat.trim().replace(/\./g, '').replace(',', '.');;
    }

    private increment(node :HTMLElement, current :number = 0) :void
    {
        const target :number = parseFloat(node.dataset.target || '');
        const step   :number = target / this.steps;
        setTimeout(() => {
            if (0 === parseInt(node.dataset.decimals || '')) {
                current = Math.min(target, current + step);
                node.innerHTML = Math.round(current).toLocaleString('de-DE');
            }
            else {
                current = Math.min(target, current + step);
                node.innerHTML = current.toFixed(parseInt(node.dataset.decimals || '')).replace(/\./g, ',');
            }
            if (node.dataset.prefix) {
                node.innerHTML = `${node.dataset.prefix}${this.spaceBeforeUnit ? '&nbsp;' : ''}${node.innerHTML}`;
            }
            if (node.dataset.unit) {
                node.innerHTML += `${this.spaceBeforeUnit ? '&nbsp;' : ''}${node.dataset.unit}`;
            }
            if (current < target) {
                this.increment(node, current);
            }
        }, this.duration / this.steps);
    }

    /**
     * Remove the space before the unit for this Counter.
     */
    public removeSpaceBeforeUnit() :Counter
    {
        this.spaceBeforeUnit = false;
        return this;
    }

    static REGEXP_NUMBERS_AND_UNITS :RegExp =
        /^\s*(?<prefix>[><=!]+)?\s*(?<value>[+-]?(?:\d{1,3}(?:[.,]\d{3})+|\d+)(?:[.,]\d+)?)[\s]*(?<unit>[%A-Za-z€$£¥°µμ\+><=!]+)?/;

}

export default Counter;